// App.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import "./App.css";

// Register Chart.js components AFTER import statements
Chart.register(...registerables);

// Use an explicit backend in production, and the current host on port 4001 locally.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ||
  `${window.location.protocol}//${window.location.hostname}:4001`;
const STAR_API_TOKEN = process.env.REACT_APP_STAR_API_TOKEN || process.env.REACT_APP_STAR_WRITE_TOKEN || "";

const getStoredApiToken = () => localStorage.getItem("starApiToken") || STAR_API_TOKEN;

const requestConfig = () => {
  const token = getStoredApiToken();
  if (!token) {
    return {};
  }

  return {
    headers: {
      "X-Star-Api-Token": token,
      "X-Star-Write-Token": token
    }
  };
};

const promptForApiToken = () => {
  const token = window.prompt("Enter the star app access token");
  if (token && token.trim()) {
    localStorage.setItem("starApiToken", token.trim());
    return true;
  }

  return false;
};

const apiGet = async (url) => {
  try {
    return await axios.get(url, requestConfig());
  } catch (err) {
    if (err.response?.status === 401 && promptForApiToken()) {
      return axios.get(url, requestConfig());
    }
    throw err;
  }
};

const apiPost = async (url, data) => {
  try {
    return await axios.post(url, data, requestConfig());
  } catch (err) {
    if (err.response?.status === 401 && promptForApiToken()) {
      return axios.post(url, data, requestConfig());
    }
    throw err;
  }
};

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_PERIODS = ["Morning", "Afternoon", "Evening", "Bedtime", "X-tra Credit"];
const AVAILABLE_YEARS = Array.from({ length: 2033 - 2025 + 1 }, (_, i) => 2025 + i);

const THEME_OPTIONS = [
  { value: 'system', label: 'System Default' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
];

function App() {
  // No authentication; the app is public.
  const [currentTab, setCurrentTab] = useState("main"); // "main" or "report" or "preferences"
  const [year, setYear] = useState("2025");
  const [allWeeks, setAllWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [currentWeekData, setCurrentWeekData] = useState(null);
  const [weekTotals, setWeekTotals] = useState({ home: 0, school: 0 });
  const [yearTotals, setYearTotals] = useState({ home: 0, school: 0 });
  const [reportData, setReportData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [childName, setChildName] = useState(localStorage.getItem("childName") || "Liam");
  const [themePreference, setThemePreference] = useState(
    localStorage.getItem("themePreference") || "system"
  );
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [currentNotes, setCurrentNotes] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [activeNotesTab, setActiveNotesTab] = useState("general");
  const [dayPartNotes, setDayPartNotes] = useState({
    Morning: "",
    Afternoon: "",
    Evening: "",
    Bedtime: "",
    "X-tra Credit": ""
  });

  // Fetch functions (no auth needed)
  const fetchWeeksForYear = useCallback(async () => {
    try {
      const res = await apiGet(`${BACKEND_URL}/star-weeks?year=${year}`);
      setAllWeeks(res.data);
    } catch (err) {
      console.error("Error fetching weeks:", err);
    }
  }, [year]);

  const fetchWeekData = useCallback(async (weekStart) => {
    try {
      const res = await apiGet(`${BACKEND_URL}/star-data`);
      const found = res.data.weeks.find((w) => w.weekStart === weekStart);
      setCurrentWeekData(found || null);
    } catch (err) {
      console.error("Error fetching week data:", err);
    }
  }, []);

  const fetchTotals = useCallback(async () => {
    try {
      const res = await apiGet(`${BACKEND_URL}/totals?year=${year}`);
      setYearTotals({ home: res.data.yearHomeTotal, school: res.data.yearSchoolTotal });
      if (selectedWeek) {
        setWeekTotals({
          home: res.data.weekTotals[selectedWeek] || 0,
          school: res.data.schoolWeekTotals[selectedWeek] || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching totals:", err);
    }
  }, [year, selectedWeek]);

  const fetchReportData = useCallback(async () => {
    try {
      const res = await apiGet(`${BACKEND_URL}/report?year=${year}`);
      setReportData(res.data);
    } catch (err) {
      console.error("Error fetching report data:", err);
    }
  }, [year]);

  // Toggle a star cell: sends an update to /star-data with the new value.
  const toggleStar = useCallback(
    async (dayName, timePeriod, currentValue) => {
      const newValue = currentValue === 1 ? 0 : 1;
      try {
        await apiPost(`${BACKEND_URL}/star-data`, {
          weekStart: selectedWeek,
          dayName,
          timePeriod,
          stars: newValue,
        });
        // Refresh the week data and totals after update.
        fetchWeekData(selectedWeek);
        fetchTotals();
      } catch (err) {
        console.error("Error toggling star:", err);
      }
    },
    [selectedWeek, fetchWeekData, fetchTotals]
  );

  // Resize listener to update mobile state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load weeks and totals when the year or tab changes
  useEffect(() => {
    fetchWeeksForYear();
    fetchTotals();
    if (currentTab === "report") {
      fetchReportData();
    }
  }, [year, currentTab, fetchWeeksForYear, fetchTotals, fetchReportData]);

  // Automatically select the current week on load
  const getMondayOfCurrentWeek = useCallback(() => {
    const today = new Date();
    const day = today.getDay(); // Sunday=0, Monday=1, etc.
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, "0");
    const dd = String(monday.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  useEffect(() => {
    if (allWeeks.length > 0 && !selectedWeek) {
      const currentMon = getMondayOfCurrentWeek();
      if (allWeeks.includes(currentMon)) {
        setSelectedWeek(currentMon);
      } else {
        setSelectedWeek(allWeeks[allWeeks.length - 1]);
      }
    }
  }, [allWeeks, selectedWeek, getMondayOfCurrentWeek]);

  useEffect(() => {
    if (selectedWeek) {
      fetchWeekData(selectedWeek);
    } else {
      setCurrentWeekData(null);
    }
  }, [selectedWeek, fetchWeekData]);

  // Add theme management
  const applyTheme = useCallback((theme) => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const shouldUseDark = 
      theme === "dark" || (theme === "system" && prefersDark);
    
    document.documentElement.setAttribute(
      "data-theme",
      shouldUseDark ? "dark" : "light"
    );
  }, []);

  // Handle theme changes
  useEffect(() => {
    applyTheme(themePreference);
    
    if (themePreference === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [themePreference, applyTheme]);

  // Add preferences persistence
  useEffect(() => {
    localStorage.setItem("childName", childName);
    localStorage.setItem("themePreference", themePreference);
  }, [childName, themePreference]);

  // Function to open notes modal
  const openNotesModal = (day) => {
    const dayData = currentWeekData ? currentWeekData.days[day] || {} : {};
    setSelectedDay(day);
    
    // Set general notes
    setCurrentNotes(dayData.notes || "");
    
    // Set day part specific notes
    const partNotes = {};
    TIME_PERIODS.forEach(period => {
      partNotes[period] = dayData[`${period}Notes`] || "";
    });
    setDayPartNotes(partNotes);
    
    setActiveNotesTab("general");
    setNotesModalOpen(true);
  };

  // Function to save notes
  const saveNotes = async () => {
    try {
      // Prepare notes data including both general and day part specific notes
      const notesData = {
        weekStart: selectedWeek,
        dayName: selectedDay,
        notes: currentNotes,
        dayPartNotes: dayPartNotes
      };
      
      await apiPost(`${BACKEND_URL}/day-notes`, notesData);
      
      // Update local state to reflect the change
      const updatedWeekData = { ...currentWeekData };
      updatedWeekData.days[selectedDay].notes = currentNotes;
      
      // Update day part specific notes
      TIME_PERIODS.forEach(period => {
        updatedWeekData.days[selectedDay][`${period}Notes`] = dayPartNotes[period];
      });
      
      setCurrentWeekData(updatedWeekData);
      setNotesModalOpen(false);
    } catch (err) {
      console.error("Error saving notes:", err);
    }
  };

  // Function to handle day part note changes
  const handleDayPartNoteChange = (period, value) => {
    setDayPartNotes(prev => ({
      ...prev,
      [period]: value
    }));
  };

  // Helper function to check if any notes exist for a day
  const hasAnyNotes = (dayData) => {
    if (!dayData) return false;
    
    // Check general notes
    if (dayData.notes && dayData.notes.trim() !== "") return true;
    
    // Check day part specific notes
    for (const period of TIME_PERIODS) {
      const periodNotes = dayData[`${period}Notes`];
      if (periodNotes && periodNotes.trim() !== "") {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to calculate day total (excluding notes fields)
  const calculateDayTotal = (dayData) => {
    if (!dayData) return 0;
    
    let total = 0;
    TIME_PERIODS.forEach(period => {
      total += dayData[period] || 0;
    });
    
    return total;
  };

  // Render Main (Home) View
  const renderMainView = () => {
    return (
      <div>
        <div className="tab-bar">
          <button className={currentTab === "main" ? "active" : ""} onClick={() => setCurrentTab("main")}>
            Home
          </button>
          <button className={currentTab === "report" ? "active" : ""} onClick={() => setCurrentTab("report")}>
            Reporting
          </button>
          <button className={currentTab === "preferences" ? "active" : ""} onClick={() => setCurrentTab("preferences")}>
            Preferences
          </button>
        </div>
        <div className="totals-grid">
          <div className="total-box">
            <span className="total-label">Home Stars This Week</span>
            <span className="total-value">{weekTotals.home}</span>
          </div>
          <div className="total-box">
            <span className="total-label">School Stars This Week</span>
            <span className="total-value">{weekTotals.school}</span>
          </div>
          <div className="total-box">
            <span className="total-label">Home Stars This Year</span>
            <span className="total-value">{yearTotals.home}</span>
          </div>
          <div className="total-box">
            <span className="total-label">School Stars This Year</span>
            <span className="total-value">{yearTotals.school}</span>
          </div>
        </div>
        <div className="selectors">
          <div className="year-selector">
            <label>Year:</label>
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              {AVAILABLE_YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="week-selector">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  const currentIndex = allWeeks.indexOf(selectedWeek);
                  if (currentIndex > 0) {
                    setSelectedWeek(allWeeks[currentIndex - 1]);
                  }
                }}
                disabled={allWeeks.indexOf(selectedWeek) === 0}
                className="week-nav-button"
                aria-label="Previous week"
              >
                ⬅
              </button>

              <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}>
                {allWeeks.map((ws) => (
                  <option key={ws} value={ws}>
                    {ws}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  const currentIndex = allWeeks.indexOf(selectedWeek);
                  if (currentIndex < allWeeks.length - 1) {
                    setSelectedWeek(allWeeks[currentIndex + 1]);
                  }
                }}
                disabled={allWeeks.indexOf(selectedWeek) === allWeeks.length - 1}
                className="week-nav-button"
                aria-label="Next week"
              >
                ➡
              </button>
            </div>
          </div>
        </div>
        {isMobile ? (
          // Mobile: Render a card for each day
          <div className="mobile-day-cards">
            {DAY_NAMES.map((day) => {
              const dayData = currentWeekData ? currentWeekData.days[day] : null;
              return (
                <div key={day} className="day-card">
                  <h2>
                    {day}
                    <span 
                      className="notes-icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        openNotesModal(day);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill={hasAnyNotes(dayData) ? "var(--primary-color)" : "none"} xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.5 3.5H4.5C3.95 3.5 3.5 3.95 3.5 4.5V19.5C3.5 20.05 3.95 20.5 4.5 20.5H19.5C20.05 20.5 20.5 20.05 20.5 19.5V4.5C20.5 3.95 20.05 3.5 19.5 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 7H17" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M7 12H17" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M7 17H12" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  </h2>
                  {dayData ? (
                    TIME_PERIODS.map((period) => {
                      const val = dayData[period] || 0;
                      return (
                        <div key={period} className="day-part">
                          <span className="part-label">{period}:</span>
                          <span
                            className={`${val === 1 ? "gold-star" : "black-dot"}`}
                            onClick={() => toggleStar(day, period, val)}
                          >
                            {val === 1 ? "★" : "●"}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-data">No data</p>
                  )}
                  {dayData && (
                    <div className="day-total">
                      <strong>Total: </strong>
                      <span>{calculateDayTotal(dayData)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Desktop: Render table view
          <div className="week-table-container">
            <table className="star-table">
              <thead>
                <tr>
                  <th>Day</th>
                  {TIME_PERIODS.map((period) => (
                    <th key={period}>{period}</th>
                  ))}
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {DAY_NAMES.map((day) => {
                  const dayData = currentWeekData ? currentWeekData.days[day] || {} : {};
                  return (
                    <tr key={day}>
                      <td className="day-name">{day}</td>
                      {TIME_PERIODS.map((period) => {
                        const val = dayData[period] || 0;
                        return (
                          <td
                            key={period}
                            className={`${val === 1 ? "gold-star" : "black-dot"}`}
                            onClick={() => toggleStar(day, period, val)}
                          >
                            {val === 1 ? "★" : "●"}
                          </td>
                        );
                      })}
                      <td>
                        <span 
                          className="notes-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            openNotesModal(day);
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill={hasAnyNotes(dayData) ? "var(--primary-color)" : "none"} xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.5 3.5H4.5C3.95 3.5 3.5 3.95 3.5 4.5V19.5C3.5 20.05 3.95 20.5 4.5 20.5H19.5C20.05 20.5 20.5 20.05 20.5 19.5V4.5C20.5 3.95 20.05 3.5 19.5 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7 7H17" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 12H17" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 17H12" stroke={hasAnyNotes(dayData) ? "white" : "currentColor"} strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="school-stars-container">
          <label>School Stars:</label>
          <select
            value={currentWeekData ? currentWeekData.schoolStars : 0}
            onChange={(e) => {
              const newVal = e.target.value;
              apiPost(`${BACKEND_URL}/school-stars`, { weekStart: selectedWeek, schoolStars: newVal })
                .then(() => fetchTotals())
                .catch((err) => console.error(err));
            }}
          >
            {Array.from({ length: 31 }, (_, i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
        
        {/* Notes Modal with Tabs */}
        {notesModalOpen && (
          <div className="notes-modal-backdrop" onClick={() => setNotesModalOpen(false)}>
            <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
              <div className="notes-modal-header">
                <h3 className="notes-modal-title">Notes for {selectedDay}</h3>
                <button className="notes-modal-close" onClick={() => setNotesModalOpen(false)}>×</button>
              </div>
              
              <div className="notes-tabs">
                <button 
                  className={`notes-tab general-tab ${activeNotesTab === 'general' ? 'active' : ''} ${currentNotes.trim() !== '' ? 'has-content' : ''}`}
                  onClick={() => setActiveNotesTab('general')}
                >
                  General
                </button>
                
                {/* First row of day parts */}
                {TIME_PERIODS.slice(0, 3).map(period => (
                  <button 
                    key={period}
                    className={`notes-tab ${activeNotesTab === period ? 'active' : ''} ${dayPartNotes[period]?.trim() !== '' ? 'has-content' : ''}`}
                    onClick={() => setActiveNotesTab(period)}
                  >
                    {period}
                  </button>
                ))}
                
                {/* Second row of day parts */}
                {TIME_PERIODS.slice(3).map(period => (
                  <button 
                    key={period}
                    className={`notes-tab ${activeNotesTab === period ? 'active' : ''} ${dayPartNotes[period]?.trim() !== '' ? 'has-content' : ''}`}
                    onClick={() => setActiveNotesTab(period)}
                  >
                    {period}
                  </button>
                ))}
              </div>
              
              <div className={`notes-tab-content ${activeNotesTab === 'general' ? 'active' : ''}`}>
                <textarea
                  className="notes-textarea"
                  value={currentNotes}
                  onChange={(e) => setCurrentNotes(e.target.value)}
                  placeholder="Add your general notes here..."
                />
              </div>
              
              {TIME_PERIODS.map(period => (
                <div 
                  key={period}
                  className={`notes-tab-content ${activeNotesTab === period ? 'active' : ''}`}
                >
                  <textarea
                    className="notes-textarea"
                    value={dayPartNotes[period]}
                    onChange={(e) => handleDayPartNoteChange(period, e.target.value)}
                    placeholder={`Add your ${period} notes here...`}
                  />
                </div>
              ))}
              
              <button className="notes-save-button" onClick={saveNotes}>
                Save Notes
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Reporting View
  const renderReportView = () => {
    const validWeeks = Object.keys(reportData?.weeklyHomeStars || {}).filter(
      week => reportData.weeklyHomeStars[week] || reportData.weeklySchoolStars[week]
    );

    // Define colors based on theme
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const homeStarColor = isDark ? "rgba(66,133,244,0.9)" : "rgba(26,115,232,0.8)";
    const schoolStarColor = isDark ? "rgba(52,168,83,0.9)" : "rgba(52,168,83,0.8)";
    const gridColor = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)";
    const textColor = isDark ? "#ffffff" : "#202124";
    
    const chartOptions = {
      scales: {
        x: { 
          ticks: { 
            autoSkip: false, 
            color: textColor
          },
          grid: {
            color: gridColor,
            drawBorder: true,
            borderColor: gridColor
          }
        },
        y: { 
          beginAtZero: true, 
          ticks: { 
            color: textColor
          },
          grid: {
            color: gridColor,
            drawBorder: true,
            borderColor: gridColor
          }
        }
      },
      plugins: { 
        legend: { 
          labels: { 
            color: textColor,
            font: {
              size: 12
            }
          } 
        },
        tooltip: {
          backgroundColor: isDark ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)",
          titleColor: textColor,
          bodyColor: textColor,
          borderColor: gridColor,
          borderWidth: 1
        }
      },
      elements: { 
        bar: { 
          borderRadius: 4 
        },
        line: {
          borderWidth: 2,
          tension: 0.4
        },
        point: {
          radius: 4,
          backgroundColor: isDark ? "#202124" : "#ffffff",
          borderWidth: 2
        }
      }
    };

    return (
      <div className="report-container">
        <div className="tab-bar">
          <button className={currentTab === "main" ? "active" : ""} onClick={() => setCurrentTab("main")}>
            Home
          </button>
          <button className={currentTab === "report" ? "active" : ""} onClick={() => setCurrentTab("report")}>
            Reporting
          </button>
          <button className={currentTab === "preferences" ? "active" : ""} onClick={() => setCurrentTab("preferences")}>
            Preferences
          </button>
        </div>
        {reportData === null ? (
          <p>Loading report data...</p>
        ) : (
          <>
            <div className="graph-section">
              <h3>Weekly Stars</h3>
              <Bar
                data={{
                  labels: validWeeks,
                  datasets: [
                    {
                      label: "Home Stars",
                      data: validWeeks.map(week => reportData.weeklyHomeStars[week]),
                      backgroundColor: homeStarColor,
                      borderColor: homeStarColor,
                      borderWidth: 1,
                    },
                    {
                      label: "School Stars",
                      data: validWeeks.map(week => reportData.weeklySchoolStars[week]),
                      backgroundColor: schoolStarColor,
                      borderColor: schoolStarColor,
                      borderWidth: 1,
                    },
                  ],
                }}
                options={chartOptions}
              />
            </div>

            <div className="graph-section">
              <h3>Cumulative Stars</h3>
              <Line
                data={{
                  labels: validWeeks,
                  datasets: [
                    {
                      label: "Cumulative Home Stars",
                      data: validWeeks.map(week => reportData.cumulativeHomeStars[week]),
                      borderColor: homeStarColor,
                      backgroundColor: homeStarColor,
                      fill: false,
                      pointBackgroundColor: homeStarColor,
                    },
                    {
                      label: "Cumulative School Stars",
                      data: validWeeks.map(week => reportData.cumulativeSchoolStars[week]),
                      borderColor: schoolStarColor,
                      backgroundColor: schoolStarColor,
                      fill: false,
                      pointBackgroundColor: schoolStarColor,
                    },
                  ],
                }}
                options={chartOptions}
              />
            </div>

            <div className="analytics-section">
              <h3>Weekly Average By Day</h3>
              <div className="analytics-grid">
                {reportData.analytics.averageHomeStarsPerDayOfWeek &&
                  Object.entries(reportData.analytics.averageHomeStarsPerDayOfWeek).map(([day, avg]) => (
                    <div key={day} className="analytics-card">
                      <span className="analytics-title">Avg {day}</span>
                      <span className="analytics-value">{avg}</span>
                    </div>
                  ))}
              </div>
              <h3>Weekly Average By Day Part</h3>
              <div className="analytics-grid">
                {reportData.analytics.averageHomeStarsPerDayPart &&
                  Object.entries(reportData.analytics.averageHomeStarsPerDayPart).map(([part, avg]) => (
                    <div key={part} className="analytics-card">
                      <span className="analytics-title">Avg {part}</span>
                      <span className="analytics-value">{avg}</span>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Add renderPreferencesView
  const renderPreferencesView = () => {
    return (
      <div>
        <div className="tab-bar">
          <button className={currentTab === "main" ? "active" : ""} onClick={() => setCurrentTab("main")}>
            Home
          </button>
          <button className={currentTab === "report" ? "active" : ""} onClick={() => setCurrentTab("report")}>
            Reporting
          </button>
          <button className={currentTab === "preferences" ? "active" : ""} onClick={() => setCurrentTab("preferences")}>
            Preferences
          </button>
        </div>
        
        <div className="preference-section">
          <div className="preference-title">Child's Name</div>
          <div className="preference-control">
            <input
              type="text"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="Enter child's name"
            />
          </div>
        </div>
        
        <div className="preference-section">
          <div className="preference-title">Theme</div>
          <div className="preference-control">
            <select
              value={themePreference}
              onChange={(e) => setThemePreference(e.target.value)}
            >
              {THEME_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <h1>{childName}'s A Star!</h1>
      {currentTab === "main" ? renderMainView() : 
       currentTab === "report" ? renderReportView() : 
       renderPreferencesView()}
    </div>
  );
}

export default App;
